import {loggerFactory} from "@cm/browser-log"
import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"

export class DataIntegrityChecker {
    protected static readonly logger = loggerFactory.getLogger("image-op-system")
    constructor(private dirHandle: FileSystemDirectoryHandle) {}

    async check(id: string, data: Float32Array) {
        const filename = `${id}.bin`
        let promiseSerializer = this.promiseSerializerByFilename.get(filename)
        if (!promiseSerializer) {
            promiseSerializer = new AsyncReentrancyGuard.PromiseSerializer()
            this.promiseSerializerByFilename.set(filename, promiseSerializer)
        }
        await promiseSerializer.executeSequentially(() => this.execute(filename, data))
    }

    private async execute(filename: string, data: Float32Array) {
        if (await this.existsFile(filename)) {
            DataIntegrityChecker.logger.debug(`File ${filename} exists, verifying data integrity...`)
            const existingData = await this.readFile(filename)
            if (existingData.length !== data.length) {
                DataIntegrityChecker.logger.error(`Data length mismatch for ${filename}: expected ${data.length}, got ${existingData.length}`)
                return
            }
            for (let i = 0; i < data.length; i++) {
                if (existingData[i] !== data[i]) {
                    DataIntegrityChecker.logger.error(`Data mismatch for ${filename} at index ${i}: expected ${data[i]}, got ${existingData[i]}`)
                    return
                }
            }
        } else {
            await this.saveFile(filename, data)
            DataIntegrityChecker.logger.debug(`Saved data to ${filename}`)
        }
    }

    private async existsFile(filename: string): Promise<boolean> {
        try {
            await this.dirHandle.getFileHandle(filename)
            return true
        } catch {
            return false
        }
    }

    // Save a Float32Array to a file inside the chosen directory
    private async saveFile(filename: string, data: Float32Array) {
        const fileHandle = await this.dirHandle.getFileHandle(filename, {create: true})
        const writable = await fileHandle.createWritable()
        await writable.write(data as Float32Array<ArrayBuffer>)
        await writable.close()
    }

    // Read a Float32Array back from the chosen directory
    private async readFile(filename: string): Promise<Float32Array> {
        const fileHandle = await this.dirHandle.getFileHandle(filename)
        const file = await fileHandle.getFile()
        const buffer = await file.arrayBuffer()
        return new Float32Array(buffer)
    }

    private promiseSerializerByFilename = new Map<string, AsyncReentrancyGuard.PromiseSerializer>()
}

export let dataIntegrityChecker: DataIntegrityChecker | null = null

export const enableDataIntegrityChecker = async (enable: boolean) => {
    if (enable && !dataIntegrityChecker) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dirHandle: FileSystemDirectoryHandle = await (window as any).showDirectoryPicker()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const perm = await (dirHandle as any).requestPermission({mode: "readwrite"})
        if (perm !== "granted") {
            throw new Error("Write permission not granted")
        }
        dataIntegrityChecker = new DataIntegrityChecker(dirHandle)
    } else if (!enable && dataIntegrityChecker) {
        dataIntegrityChecker = null
    }
}
